import React, { useState, useEffect, useRef } from 'react';
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  Button,
  Divider,
  List,
  CircularProgress,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsNone as NotificationsNoneIcon,
} from '@mui/icons-material';
import { NotificationItem } from './NotificationItem';
import { Notification } from '@/types';
import { NotificationService } from '@/services/notificationService';
import { useAuthStore } from '@/store/authStore';

interface NotificationBellProps {
  maxNotifications?: number;
}

const NotificationBell: React.FC<NotificationBellProps> = ({
  maxNotifications = 10,
}) => {
  const { user } = useAuthStore();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const unsubscribeCountRef = useRef<(() => void) | null>(null);

  const open = Boolean(anchorEl);

  useEffect(() => {
    if (!user) return;

    // Subscribe to notifications
    const unsubscribe = NotificationService.subscribeToNotifications(
      user.id,
      (notifs) => {
        setNotifications(notifs.slice(0, maxNotifications));
      },
      (error) => {
        console.error('Error subscribing to notifications:', error);
      }
    );
    unsubscribeRef.current = unsubscribe;

    // Subscribe to unread count
    const unsubscribeCount = NotificationService.subscribeToUnreadCount(
      user.id,
      (count) => {
        setUnreadCount(count);
      },
      (error) => {
        console.error('Error subscribing to unread count:', error);
      }
    );
    unsubscribeCountRef.current = unsubscribeCount;

    // Initial load
    loadNotifications();

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
      if (unsubscribeCountRef.current) {
        unsubscribeCountRef.current();
      }
    };
  }, [user, maxNotifications]);

  const loadNotifications = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const result = await NotificationService.getNotifications(user.id, {
        limit: maxNotifications,
      });
      setNotifications(result.notifications);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAllAsRead = async () => {
    if (!user) return;

    try {
      await NotificationService.markAllAsRead(user.id);
      // The subscription will update the count automatically
    } catch (error: any) {
      alert(`Failed to mark all as read: ${error.message}`);
    }
  };


  if (!user) {
    return null;
  }

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        aria-label="notifications"
        aria-describedby={open ? 'notification-popover' : undefined}
      >
        <Badge badgeContent={unreadCount} color="error">
          {unreadCount > 0 ? <NotificationsIcon /> : <NotificationsNoneIcon />}
        </Badge>
      </IconButton>

      <Popover
        id="notification-popover"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 600,
            mt: 1,
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h6">Notifications</Typography>
            {unreadCount > 0 && (
              <Button size="small" onClick={handleMarkAllAsRead}>
                Mark all as read
              </Button>
            )}
          </Box>

          <Divider sx={{ mb: 2 }} />

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <NotificationsNoneIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                No notifications
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={(id: string) => NotificationService.markAsRead(id)}
                  onDelete={(id: string) => NotificationService.deleteNotification(id)}
                />
              ))}
            </List>
          )}

          {notifications.length > 0 && (
            <>
              <Divider sx={{ mt: 2, mb: 2 }} />
              <Box sx={{ textAlign: 'center' }}>
                <Button
                  size="small"
                  onClick={() => {
                    handleClose();
                    // Navigate to full notifications page if it exists
                    // window.location.href = '/notifications';
                  }}
                >
                  View All Notifications
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default NotificationBell;

